import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Edit2, Trash2, X, Save, BellRing } from "lucide-react";
import MarkdownEditor from "@/components/MarkdownEditor";
import ImageUploader from "@/components/ImageUploader";
import MultiImageUploader from "@/components/MultiImageUploader";

// Convert a stored ISO 8601 UTC string (`2026-09-01T13:00:00+00:00`) into the
// local-time `YYYY-MM-DDTHH:mm` value that <input type="datetime-local">
// expects. Empty in → empty out so the field renders as blank.
function _isoToLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
// Convert the local-datetime input value back to an ISO 8601 string with
// the browser's UTC offset. Backend normalizes anyway, but we send a
// well-formed value so nothing has to guess.
function _localToISO(local) {
  if (!local) return "";
  try {
    const d = new Date(local);
    if (Number.isNaN(d.getTime())) return local;
    return d.toISOString();
  } catch { return local; }
}

const EMPTY = {
  name: "", slug: "", description: "", story: "", image_url: "", images: [],
  farm_name: "", vendor_id: "", price: 0, cogs: "", total_inventory: 0,
  drops_at: "", ends_at: "", early_access_premium_hours: 48, visible: true,
  master_box_id: "", cooking_instructions: "", cooking_video_url: "",
};

function DropForm({ initial, onSave, onCancel, isNew }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const setVal = (k, v) => setF(p => ({ ...p, [k]: v }));

  // Soft client-side guard for the cogs ≤ price invariant. The backend still
  // enforces hard validation (HTTP 400) — this is just a friendly nudge.
  const priceNum = parseFloat(f.price) || 0;
  const cogsNum = f.cogs === "" || f.cogs === null || f.cogs === undefined
    ? null : parseFloat(f.cogs);
  const cogsWarning = cogsNum !== null && Number.isFinite(cogsNum) && cogsNum > priceNum
    ? `COGS ($${cogsNum.toFixed(2)}) is higher than price ($${priceNum.toFixed(2)}). Save will be rejected.`
    : null;

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const cogsParsed = f.cogs === "" || f.cogs === null || f.cogs === undefined
        ? null
        : parseFloat(f.cogs);
      const payload = {
        ...f,
        price: parseFloat(f.price),
        cogs: cogsParsed,
        total_inventory: parseInt(f.total_inventory || 0),
        early_access_premium_hours: parseInt(f.early_access_premium_hours || 48),
      };
      if (isNew) await api.post("/drops/admin", payload);
      else await api.put(`/drops/admin/${initial.slug}`, payload);
      onSave();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-flat p-6 mt-4" data-testid="drop-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl">{isNew ? "New drop" : `Edit ${initial.slug}`}</h3>
        <button onClick={onCancel} className="text-[var(--ff-text-muted)] hover:text-[var(--ff-text-primary)]" data-testid="drop-form-close"><X size={18}/></button>
      </div>

      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <label>Name<input className="input-base mt-1" value={f.name} onChange={e=>setVal("name",e.target.value)} data-testid="df-name"/></label>
        <label>Slug<input className="input-base mt-1" value={f.slug} onChange={e=>setVal("slug",e.target.value)} disabled={!isNew} data-testid="df-slug"/></label>
        <label className="md:col-span-2">Description<input className="input-base mt-1" value={f.description} onChange={e=>setVal("description",e.target.value)} data-testid="df-desc"/></label>
        <label className="md:col-span-2">Story (markdown)
          <div className="mt-1">
            <MarkdownEditor value={f.story} onChange={(v) => setVal("story", v)} testId="df-story-md" rows={8} placeholder="Tell the story of this drop — the farm, the harvest, the people behind it."/>
          </div>
        </label>
        <label className="md:col-span-2">Cooking instructions (markdown)
          <div className="mt-1">
            <MarkdownEditor value={f.cooking_instructions} onChange={(v) => setVal("cooking_instructions", v)} testId="df-cooking-md" rows={6} placeholder="Chef guide: sear temp, resting time, seasoning, plating. Shown under a &quot;How to cook it&quot; card on the drop page."/>
          </div>
        </label>
        <label className="md:col-span-2">Cook-along video URL (YouTube or direct mp4)
          <input
            type="url"
            className="input-base mt-1"
            placeholder="https://youtube.com/watch?v=... — 60-second cook-along clip shown next to the instructions"
            value={f.cooking_video_url}
            onChange={e => setVal("cooking_video_url", e.target.value)}
            data-testid="df-cook-along-url"
          />
        </label>
        <label className="md:col-span-2">Drop image
          <div className="mt-1">
            <ImageUploader value={f.image_url} onChange={(url) => setVal("image_url", url)} folder="drops" testId="df-image-upload"/>
          </div>
        </label>
        <label className="md:col-span-2">Additional photos (gallery)
          <div className="mt-1">
            <MultiImageUploader value={f.images || []} onChange={(arr) => setVal("images", arr)} folder="drops" testId="df-gallery"/>
          </div>
        </label>
        <label>Farm name<input className="input-base mt-1" value={f.farm_name} onChange={e=>setVal("farm_name",e.target.value)} data-testid="df-farm"/></label>
        <label>Price ($)<input type="number" step="0.01" className="input-base mt-1" value={f.price} onChange={e=>setVal("price",e.target.value)} data-testid="df-price"/></label>
        <label>
          COGS per unit ($)
          <input
            type="number"
            step="0.01"
            className="input-base mt-1"
            value={f.cogs ?? ""}
            placeholder="Cost of goods (optional)"
            onChange={e=>setVal("cogs", e.target.value)}
            data-testid="df-cogs"
          />
          <span className="block mt-1 text-xs text-[var(--ff-text-muted)]">Drives ambassador net-profit. Leave blank to fall back to 55% of price.</span>
          {cogsWarning && (
            <p className="mt-2 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="df-cogs-warn">
              {cogsWarning}
            </p>
          )}
        </label>
        <label>Total inventory<input type="number" className="input-base mt-1" value={f.total_inventory} onChange={e=>setVal("total_inventory",e.target.value)} data-testid="df-inv"/></label>
        <label className="col-span-1">
          <span>Drops at (start)</span>
          <input
            type="datetime-local"
            className="input-base mt-1"
            value={_isoToLocal(f.drops_at)}
            onChange={e => setVal("drops_at", _localToISO(e.target.value))}
            data-testid="df-dropsat"
          />
          <span className="text-[10px] text-[var(--ff-text-muted)] mt-1 block">Local time · saved as UTC</span>
        </label>
        <label className="col-span-1">
          <span>Ends at</span>
          <input
            type="datetime-local"
            className="input-base mt-1"
            value={_isoToLocal(f.ends_at)}
            onChange={e => setVal("ends_at", _localToISO(e.target.value))}
            data-testid="df-endsat"
          />
          <span className="text-[10px] text-[var(--ff-text-muted)] mt-1 block">Leave blank for no end</span>
        </label>
        <label className="col-span-2">
          <span>Master box template ID (optional)</span>
          <input
            className="input-base mt-1 font-mono text-xs"
            placeholder="Paste the box id from /warehouse → Master boxes"
            value={f.master_box_id || ""}
            onChange={e => setVal("master_box_id", e.target.value)}
            data-testid="df-master-box-id"
          />
          <span className="text-[10px] text-[var(--ff-text-muted)] mt-1 block">
            When set, each paid reservation auto-generates a packing checklist from this template.
          </span>
        </label>
        <label>Premium early-access hours<input type="number" className="input-base mt-1" value={f.early_access_premium_hours} onChange={e=>setVal("early_access_premium_hours",e.target.value)} data-testid="df-early"/></label>
        <label className="flex items-center gap-2 mt-2">
          <input type="checkbox" checked={!!f.visible} onChange={e=>setVal("visible",e.target.checked)} data-testid="df-visible"/>
          Visible to public
        </label>
      </div>

      {err && <p className="mt-3 text-red-700 text-sm" data-testid="drop-form-err">{err}</p>}

      <div className="mt-5 flex gap-2 justify-end">
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
        <button onClick={submit} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="drop-form-save">
          <Save size={16}/> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

export default function AdminDropsTab() {
  const [drops, setDrops] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | dropObj
  const [err, setErr] = useState("");
  const [notifyMsg, setNotifyMsg] = useState("");

  const load = () => api.get("/drops/admin/all").then(r => setDrops(r.data)).catch(e => setErr(e.response?.data?.detail || "Load failed"));
  useEffect(() => { load(); }, []);

  const del = async (slug) => {
    if (!confirm(`Delete drop "${slug}"?`)) return;
    try { await api.delete(`/drops/admin/${slug}`); load(); }
    catch (e) { setErr(e.response?.data?.detail || "Delete failed"); }
  };

  const notifyWaitlist = async (slug) => {
    if (!confirm(`Notify waitlist that "${slug}" is back in stock?\n\nThis is idempotent — anyone already notified will be skipped.`)) return;
    setNotifyMsg("");
    try {
      const { data } = await api.post(`/admin/drops/${slug}/waitlist/notify-restock`, {});
      const s = data.summary;
      setNotifyMsg(`✓ ${slug}: ${s.sms_sent} SMS · ${s.email_sent} email · ${s.skipped_already} skipped (${data.waitlist_size} on waitlist)`);
    } catch (e) {
      setErr(e.response?.data?.detail || "Notify failed");
    }
  };

  return (
    <div data-testid="admin-drops-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="overline">Drop calendar</p>
          <h2 className="font-display text-3xl mt-1">Schedule drops.</h2>
        </div>
        <button onClick={() => setEditing("new")} className="btn-primary inline-flex items-center gap-2" data-testid="admin-drops-new-btn">
          <Plus size={16}/> New drop
        </button>
      </div>

      {err && <p className="text-red-700 mb-3 text-sm">{err}</p>}

      <div className="card-flat overflow-x-auto" data-testid="admin-drops-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[var(--ff-text-muted)] border-b border-[var(--ff-border)]">
              <th className="py-3 px-4 font-medium">Drop</th>
              <th className="py-3 px-4 font-medium">Drops at</th>
              <th className="py-3 px-4 font-medium">Status</th>
              <th className="py-3 px-4 font-medium text-right">Inventory</th>
              <th className="py-3 px-4 font-medium text-right">Price</th>
              <th className="py-3 px-4 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {drops.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--ff-text-muted)]">No drops yet. Click "New drop" to schedule one.</td></tr>
            )}
            {drops.map(d => (
              <tr key={d.slug} className="border-b border-[var(--ff-border)] last:border-0" data-testid={`admin-drop-row-${d.slug}`}>
                <td className="py-3 px-4">
                  <div className="font-medium">{d.name}</div>
                  <div className="text-xs text-[var(--ff-text-muted)] font-mono">/{d.slug} · {d.farm_name}</div>
                </td>
                <td className="py-3 px-4 font-mono text-xs">{(d.drops_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-0.5 text-[10px] rounded-full font-semibold uppercase ${d.status === "live" ? "bg-[var(--ff-primary)] text-white" : d.status === "sold_out" ? "bg-red-900 text-white" : d.status === "ended" ? "bg-[var(--ff-border)] text-[var(--ff-text-muted)]" : "bg-[var(--ff-muted)]"}`}>{d.status}</span>
                  {!d.visible && <span className="ml-2 text-[10px] text-[var(--ff-text-muted)]">hidden</span>}
                </td>
                <td className="py-3 px-4 text-right tabular-nums">{d.remaining}/{d.total_inventory}</td>
                <td className="py-3 px-4 text-right tabular-nums">${d.price?.toFixed(2)}</td>
                <td className="py-3 px-4 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => notifyWaitlist(d.slug)} className="text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]" title="Notify waitlist of restock" data-testid={`admin-drop-notify-${d.slug}`}><BellRing size={14}/></button>
                    <button onClick={() => setEditing(d)} className="text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]" data-testid={`admin-drop-edit-${d.slug}`}><Edit2 size={14}/></button>
                    <button onClick={() => del(d.slug)} className="text-[var(--ff-text-secondary)] hover:text-red-700" data-testid={`admin-drop-del-${d.slug}`}><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <DropForm
          initial={editing === "new" ? {} : editing}
          isNew={editing === "new"}
          onSave={() => { setEditing(null); load(); }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}
